/* Admin "AI activity": every API token (who owns it, which AI uses it) and
 * everything the AI did through it -- from the api_token_activity log
 * (backend/middleware/api_token_activity.py). Changes carry a readable
 * "what changed" (visuals added / changed / removed, pages, revision). */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Bot, Clock, KeyRound, PencilLine, RefreshCw, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { apiTokensAdminApi, type ActivityItem, type AdminApiToken, type ActivityFilters } from "../../api/apiTokensAdmin";
import {
  Avatar,
  Badge,
  Button,
  Card,
  DataTable,
  Drawer,
  EmptyState,
  PageHeader,
  Select,
  SkeletonRows,
  Stat,
  Switch,
  Tabs,
  formatDateTime,
  formatRelative,
  useConfirm,
  useToast,
  type Tone,
} from "../../ds";
import "./AiActivityPage.css";

const STATUS_TONE: Record<AdminApiToken["status"], Tone> = { active: "success", expired: "neutral", revoked: "danger" };
const SCOPE_LABEL: Record<string, string> = {
  "datasets:read": "Read data",
  "reports:read": "Read reports",
  "drafts:write": "Edit drafts",
  preview: "Preview",
  publish: "Publish",
};

function ClientBadge({ client }: { client: string }) {
  const tone: Tone = /claude/i.test(client) ? "accent" : /chatgpt|codex/i.test(client) ? "success" : "neutral";
  return <Badge tone={tone}><Bot size={11} /> {client}</Badge>;
}

export function AiActivityPage() {
  const [tab, setTab] = useState<"activity" | "tokens">("activity");
  const [filters, setFilters] = useState<ActivityFilters>({});
  const [open, setOpen] = useState<ActivityItem | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const tokens = useQuery({ queryKey: ["admin-api-tokens"], queryFn: apiTokensAdminApi.list });
  const summary = useQuery({ queryKey: ["admin-api-tokens", "summary"], queryFn: apiTokensAdminApi.summary });
  const activity = useInfiniteQuery({
    queryKey: ["admin-api-tokens", "activity", filters],
    queryFn: ({ pageParam }) => apiTokensAdminApi.activity({ ...filters, before: pageParam ?? undefined, limit: 100 }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_before,
  });
  const items = activity.data?.pages.flatMap((p) => p.items) ?? [];

  const revoke = useMutation({
    mutationFn: (t: AdminApiToken) => apiTokensAdminApi.revoke(t.id),
    onSuccess: (t) => {
      toast.success("Token revoked", `“${t.name}” (${t.owner_name ?? "unknown"}) can no longer reach the platform.`);
      qc.invalidateQueries({ queryKey: ["admin-api-tokens"] });
    },
    onError: (e: Error) => toast.error("Couldn't revoke the token", e.message),
  });

  const people = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tokens.data ?? []) m.set(t.owner_id, t.owner_name ?? t.owner_email ?? "Unknown");
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [tokens.data]);
  const clients = useMemo(() => [...new Set([...(summary.data?.clients_7d ?? []).map((c) => c.client), ...(tokens.data ?? []).flatMap((t) => t.clients)])].sort(), [summary.data, tokens.data]);

  const setFilter = (patch: Partial<ActivityFilters>) => setFilters((f) => ({ ...f, ...patch }));
  const showTokenActivity = (t: AdminApiToken) => {
    setFilters({ token_id: t.id });
    setTab("activity");
  };
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-api-tokens"] });
  const s = summary.data;

  return (
    <div className="ds-page">
      <PageHeader
        title="AI activity"
        subtitle="Every API token, who owns it, which AI uses it — and everything the AI did through it."
        actions={<Button icon={RefreshCw} onClick={refresh}>Refresh</Button>}
      />

      <div className="ds-grid ds-grid--4 aia-stats">
        <Stat icon={KeyRound} label="Active tokens" value={s?.active_tokens ?? "—"} loading={summary.isLoading} onClick={() => setTab("tokens")} />
        <Stat icon={Users} label="People using AI (7 days)" value={s ? `${s.people_active_7d} of ${s.people_with_tokens}` : "—"} loading={summary.isLoading} />
        <Stat icon={Clock} label="AI requests (7 days)" value={s?.requests_7d ?? "—"} loading={summary.isLoading} tone="info" onClick={() => { setFilters({}); setTab("activity"); }} />
        <Stat
          icon={PencilLine}
          label={s?.failed_7d ? `Changes (7 days) · ${s.failed_7d} failed` : "Changes (7 days)"}
          value={s?.changes_7d ?? "—"}
          loading={summary.isLoading}
          tone="success"
          onClick={() => { setFilters({ changes_only: true }); setTab("activity"); }}
        />
      </div>
      {s?.clients_7d.length ? (
        <div className="ds-row aia-clients">
          <span className="ds-small ds-faint">Last 7 days by AI:</span>
          {s.clients_7d.map((c) => (
            <button key={c.client} type="button" className="aia-client-chip" onClick={() => { setFilters({ client: c.client }); setTab("activity"); }}>
              <ClientBadge client={c.client} /> <span className="ds-small ds-muted">{c.requests}</span>
            </button>
          ))}
        </div>
      ) : null}

      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: "activity", label: "Activity" },
          { value: "tokens", label: "Tokens", count: tokens.data?.length },
        ]}
      />

      {tab === "activity" ? (
        <Card flush>
          <div className="ds-toolbar aia-filters">
            <Select value={filters.user_id ?? ""} onChange={(e) => setFilter({ user_id: e.target.value || undefined })} aria-label="Person">
              <option value="">Everyone</option>
              {people.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </Select>
            <Select value={filters.token_id ?? ""} onChange={(e) => setFilter({ token_id: e.target.value || undefined })} aria-label="Token">
              <option value="">All tokens</option>
              {(tokens.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.name} — {t.owner_name ?? "?"}</option>)}
            </Select>
            <Select value={filters.client ?? ""} onChange={(e) => setFilter({ client: e.target.value || undefined })} aria-label="AI">
              <option value="">Any AI</option>
              {clients.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
            <Switch label="Changes only" checked={!!filters.changes_only} onChange={(v) => setFilter({ changes_only: v || undefined })} />
            <Switch label="Failed only" checked={!!filters.failed_only} onChange={(v) => setFilter({ failed_only: v || undefined })} />
            <div className="ds-grow" />
            {Object.values(filters).some(Boolean) ? <Button size="sm" variant="ghost" onClick={() => setFilters({})}>Clear filters</Button> : null}
          </div>

          {activity.isLoading ? (
            <div style={{ padding: 16 }}><SkeletonRows rows={6} /></div>
          ) : items.length === 0 ? (
            <EmptyState icon={Bot} title="No AI activity yet" description="Requests made with an API token (Claude, ChatGPT, Codex…) appear here as they happen." />
          ) : (
            <ol className="aia-feed">
              {items.map((a) => (
                <li key={a.id}>
                  <button type="button" className={`aia-row${a.is_change ? " aia-row--change" : ""}${a.status_code >= 400 ? " aia-row--failed" : ""}`} onClick={() => setOpen(a)}>
                    <span className="aia-row__time ds-small ds-faint" title={formatDateTime(a.created_at)}>{formatRelative(a.created_at)}</span>
                    <Avatar name={a.owner_name} />
                    <span className="aia-row__main">
                      <span className="aia-row__who">
                        <strong>{a.owner_name ?? "Unknown"}</strong>
                        <ClientBadge client={a.client} />
                        <span className="ds-small ds-faint">via “{a.token_name ?? a.token_prefix}”</span>
                      </span>
                      <span className="aia-row__what">
                        {a.status_code >= 400 ? <AlertTriangle size={14} className="aia-warn" /> : a.is_change ? <PencilLine size={14} className="aia-ok" /> : null}
                        {a.summary}
                      </span>
                    </span>
                    <span className="aia-row__target ds-small">
                      {a.target_name ? <span className="ds-truncate">{a.target_name}</span> : null}
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          )}
          {activity.hasNextPage ? (
            <div style={{ padding: 12, textAlign: "center" }}>
              <Button size="sm" onClick={() => activity.fetchNextPage()} loading={activity.isFetchingNextPage}>Load older activity</Button>
            </div>
          ) : null}
        </Card>
      ) : (
        <Card flush>
          <DataTable<AdminApiToken>
            rows={tokens.data}
            loading={tokens.isLoading}
            rowKey={(t) => t.id}
            onRowClick={showTokenActivity}
            empty={<EmptyState icon={KeyRound} title="No API tokens yet" description="People create tokens under AI & API access to let Claude, ChatGPT or Codex build dashboards." />}
            columns={[
              { key: "owner", header: "Owner", sortValue: (t) => t.owner_name ?? "", cell: (t) => <span className="ds-row" style={{ gap: 8 }}><Avatar name={t.owner_name} /><span><div style={{ fontWeight: 600 }}>{t.owner_name ?? "Unknown"}</div><div className="ds-small ds-muted">{t.owner_email}</div></span></span> },
              { key: "token", header: "Token", sortValue: (t) => t.name, cell: (t) => <div style={{ minWidth: 160 }}><div style={{ fontWeight: 600 }}>{t.name} <span className="ds-small ds-mono ds-faint">{t.token_prefix}…</span></div><div className="ds-small ds-faint">Can: {t.scopes.map((x) => SCOPE_LABEL[x] ?? x).join(" · ")}</div></div> },
              { key: "ai", header: "AI used", cell: (t) => (t.clients.length ? <span className="ds-row" style={{ gap: 4, flexWrap: "wrap" }}>{t.clients.map((c) => <ClientBadge key={c} client={c} />)}</span> : <span className="ds-faint">Not used yet</span>) },
              { key: "status", header: "Status", sortValue: (t) => t.status, cell: (t) => <><Badge tone={STATUS_TONE[t.status]} dot title={t.revoked_at ? `Revoked ${formatDateTime(t.revoked_at)}` : t.expires_at ? `Expires ${formatDateTime(t.expires_at)}` : undefined}>{t.status === "active" ? "Active" : t.status === "expired" ? "Expired" : "Revoked"}</Badge>{t.status === "active" && t.expires_at ? <div className="ds-small ds-faint" style={{ marginTop: 2, whiteSpace: "nowrap" }}>expires {formatRelative(t.expires_at)}</div> : null}</> },
              { key: "used", header: "Last used", sortValue: (t) => t.last_used_at ?? "", cell: (t) => <span className="ds-muted" title={t.last_used_at ? formatDateTime(t.last_used_at) : undefined}>{t.last_used_at ? formatRelative(t.last_used_at) : "Never"}</span> },
              { key: "req", header: "Requests 7d / all", align: "right", sortValue: (t) => t.requests_total, cell: (t) => `${t.requests_7d} / ${t.requests_total}` },
              { key: "chg", header: "Changes", align: "right", sortValue: (t) => t.changes_total, cell: (t) => t.changes_total },
              {
                key: "actions",
                header: "",
                align: "right",
                cell: (t) => (
                  <span className="ds-row" style={{ gap: 4, justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" onClick={() => showTokenActivity(t)}>Activity</Button>
                    {t.status === "active" ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          if (await confirm({ title: `Revoke “${t.name}”?`, message: `${t.owner_name ?? "The owner"}'s AI stops working with this token immediately. They can create a new one.`, confirmLabel: "Revoke token", tone: "danger" })) revoke.mutate(t);
                        }}
                      >
                        Revoke
                      </Button>
                    ) : null}
                  </span>
                ),
              },
            ]}
          />
        </Card>
      )}

      <ActivityDrawer item={open} onClose={() => setOpen(null)} />
    </div>
  );
}

function WidgetList({ title, items, tone }: { title: string; items?: { id: string; page: string; label: string; what?: string[] }[]; tone: Tone }) {
  if (!items?.length) return null;
  return (
    <div className="aia-diff">
      <div className="aia-diff__title"><Badge tone={tone}>{title} · {items.length}</Badge></div>
      <ul>
        {items.map((w) => (
          <li key={w.id}>
            <span>{w.label}</span>
            <span className="ds-small ds-faint"> on {w.page}{w.what?.length ? ` — ${w.what.join(", ")}` : ""}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ActivityDrawer({ item, onClose }: { item: ActivityItem | null; onClose: () => void }) {
  const d = item?.details ?? null;
  return (
    <Drawer open={!!item} onClose={onClose} title={item?.action_label ?? ""} width={560}>
      {item ? (
        <div className="ds-stack" style={{ gap: 16 }}>
          <div>
            <div style={{ fontWeight: 600 }}>{item.summary}</div>
            <div className="ds-small ds-muted" style={{ marginTop: 4 }}>{formatDateTime(item.created_at)} · {formatRelative(item.created_at)}</div>
          </div>
          <dl className="aia-meta">
            <dt>Person</dt><dd>{item.owner_name ?? "Unknown"} <span className="ds-faint">{item.owner_email}</span></dd>
            <dt>AI</dt><dd><ClientBadge client={item.client} /></dd>
            <dt>Token</dt><dd>{item.token_name} <span className="ds-mono ds-faint">{item.token_prefix}…</span></dd>
            {item.target_id ? (
              <>
                <dt>{item.target_type === "dataset" ? "Dataset" : "Report"}</dt>
                <dd>{item.target_type === "report" ? <Link to={`/admin/reports/${item.target_id}`}>{item.target_name ?? item.target_id}</Link> : item.target_name ?? item.target_id}</dd>
              </>
            ) : null}
            <dt>Result</dt><dd><Badge tone={item.status_code >= 400 ? "danger" : "success"}>{item.status_code >= 400 ? `Failed (${item.status_code})` : "OK"}</Badge> <span className="ds-small ds-faint">{item.duration_ms} ms</span></dd>
          </dl>

          {d?.counts || d?.pagesAdded?.length || d?.pagesRemoved?.length || d?.reportSettingsChanged?.length ? (
            <Card title="What changed" subtitle={d.fromRevision !== undefined ? `Draft revision ${d.fromRevision} → ${d.toRevision}` : undefined}>
              <WidgetList title="Added" items={d.widgetsAdded} tone="success" />
              <WidgetList title="Changed" items={d.widgetsChanged} tone="info" />
              <WidgetList title="Removed" items={d.widgetsRemoved} tone="danger" />
              {d.pagesAdded?.length ? <p className="ds-small">New pages: {d.pagesAdded.join(", ")}</p> : null}
              {d.pagesRemoved?.length ? <p className="ds-small">Removed pages: {d.pagesRemoved.join(", ")}</p> : null}
              {d.pagesRenamed?.length ? <p className="ds-small">Renamed: {d.pagesRenamed.map((p) => `${p.from} → ${p.to}`).join(", ")}</p> : null}
              {d.reportSettingsChanged?.length ? <p className="ds-small">Report settings: {d.reportSettingsChanged.join(", ")}</p> : null}
              {!d.counts?.added && !d.counts?.changed && !d.counts?.removed && !d.pagesAdded?.length && !d.pagesRemoved?.length && !d.pagesRenamed?.length && !d.reportSettingsChanged?.length ? <p className="ds-small ds-muted">Saved with no differences.</p> : null}
            </Card>
          ) : null}
          {d?.errors?.length ? (
            <Card title="Why it failed">
              <ul className="ds-small">{d.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </Card>
          ) : null}
          {d?.measures ? (
            <Card title="Query">
              <p className="ds-small">Measures: {d.measures.join(", ") || "—"}<br />By: {d.dimensions?.join(", ") || "—"}</p>
            </Card>
          ) : null}

          <details className="ds-small ds-muted">
            <summary>Technical details</summary>
            <dl className="aia-meta" style={{ marginTop: 8 }}>
              <dt>Request</dt><dd className="ds-mono">{item.method} {item.path}</dd>
              <dt>IP</dt><dd className="ds-mono">{item.ip_address ?? "—"}</dd>
              <dt>User agent</dt><dd className="ds-mono" style={{ wordBreak: "break-all" }}>{item.user_agent ?? "—"}</dd>
            </dl>
          </details>
        </div>
      ) : null}
    </Drawer>
  );
}
